import { useEffect, useState } from "react";
import { toast } from "sonner";
import api, { formatErr } from "../lib/api";
import { BRAND_COLORS, withAlpha } from "../lib/bookingStatus";
import { deDate } from "../lib/dateFormat";

function StarRow({ avg }) {
  const filled = Math.round(avg);
  return (
    <span className="whitespace-nowrap" style={{ color: BRAND_COLORS.gold }}>
      {"★".repeat(filled)}<span style={{ color: withAlpha(BRAND_COLORS.navy, 0.15) }}>{"★".repeat(5 - filled)}</span>
    </span>
  );
}

export default function TeamFeedback() {
  const [data, setData] = useState(null);

  useEffect(() => {
    (async () => {
      try { const { data } = await api.get("/feedback"); setData(data); }
      catch (e) { console.error("Feedback load failed:", e); toast.error(formatErr(e.response?.data?.detail) || e.message); }
    })();
  }, []);

  if (!data) return <div className="max-w-[1200px] mx-auto p-16 text-center label-caps">Wird geladen…</div>;

  const KIND_LABEL = { task: "Aufgabe", cleaning: "Reinigung", garden: "Garten", maintenance: "Wartung" };

  return (
    <div className="max-w-[1400px] mx-auto px-6 md:px-10 py-10 md:py-14" data-testid="team-feedback">
      <div className="mb-8">
        <div className="label-caps mb-3">Weissblom Team</div>
        <h1 className="font-display text-4xl md:text-5xl text-navy">Feedback & Bewertungen</h1>
        <p className="text-[15px] text-navy-dark/70 mt-3 max-w-2xl">Durchschnitts-Sterne pro Partner und Auftragstyp — Trends und Problemzonen auf einen Blick.</p>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-3 gap-5 mb-10">
        <div className="bg-white border border-navy/10 p-6" data-testid="fb-overall">
          <div className="label-caps mb-2">Gesamt</div>
          <div className="font-display text-5xl text-navy">{data.overall_avg || "—"}</div>
          <div className="text-[13px] text-navy-dark/60 mt-1">Ø aus {data.total} Bewertungen</div>
        </div>
        <div className="bg-white border border-navy/10 p-6 col-span-2">
          <div className="label-caps mb-2">Nach Auftragstyp</div>
          {data.by_kind.length === 0 ? (
            <div className="text-[13px] text-navy-dark/50">Noch keine Bewertungen.</div>
          ) : (
            <div className="space-y-2">
              {data.by_kind.map((k) => (
                <div key={k.kind} data-testid={`fb-kind-${k.kind}`} className="flex justify-between items-center py-1.5 border-b border-navy/5 last:border-b-0">
                  <div className="text-[13px] text-navy">{KIND_LABEL[k.kind] || k.kind}</div>
                  <div className="flex items-center gap-3">
                    <StarRow avg={k.avg} />
                    <span className="text-[12px] text-navy-dark/60 tabular-nums w-16 text-right">{k.avg} · {k.count}×</span>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>

      <div className="bg-white border border-navy/10 p-6 mb-10">
        <h2 className="font-display text-2xl text-navy mb-4">Nach Partner</h2>
        {data.by_partner.length === 0 ? (
          <div className="text-[13px] text-navy-dark/50">Noch keine Bewertungen.</div>
        ) : (
          <div className="space-y-2">
            {data.by_partner.map((p) => (
              <div key={p.partner_email} data-testid={`fb-partner-${p.partner_email}`}
                className="grid grid-cols-4 gap-4 py-2 border-b border-navy/5 last:border-b-0 items-center">
                <div className="col-span-2 text-navy truncate">{p.partner_email}</div>
                <div><StarRow avg={p.avg} /></div>
                <div className="text-[12px] text-navy-dark/60 text-right">
                  {p.avg} · {p.count}× {p.low_count > 0 && <span className="ml-2" style={{ color: "#C0392B" }}>({p.low_count} niedrig)</span>}
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      <div className="bg-white border border-navy/10 p-6">
        <h2 className="font-display text-2xl text-navy mb-4">Letzte Bewertungen</h2>
        {data.recent.length === 0 ? (
          <div className="text-[13px] text-navy-dark/50">Noch keine Bewertungen.</div>
        ) : (
          <div className="space-y-3">
            {data.recent.slice(0, 20).map((r) => (
              <div key={r.id} data-testid={`fb-recent-${r.id}`} className="border-b border-navy/5 last:border-b-0 pb-3">
                <div className="flex items-center justify-between gap-3 flex-wrap">
                  <div className="font-display text-lg text-navy">{r.title}</div>
                  <StarRow avg={r.stars} />
                </div>
                <div className="text-[11px] tracking-widest uppercase text-navy-dark/50 mt-0.5">
                  {r.rated_at ? deDate(r.rated_at) : "—"} · {r.partner_email || "—"} · {KIND_LABEL[r.kind] || r.kind}
                </div>
                {r.comment && <div className="text-[13px] text-navy-dark/80 italic mt-1">„{r.comment}"</div>}
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
